import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {SortAscIcon, SortDescIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import type {RefObject} from 'react'
import type {Artifact, SortDirection, SortField, SortOrderType} from '../types'
import {number as formatNumber} from '@github-ui/formatters'
import {
  buildSortOrder,
  DEFAULT_SORT,
  parseSortOrder,
  SORT_BUTTON_LABELS,
  SORT_DIRECTION_LABELS,
  SORT_FIELD_LABELS,
} from '../types'
import {ArtifactListItem} from './ArtifactListItem'

interface ArtifactsListProps {
  org: string
  artifacts: Artifact[]
  totalCount: number
  listRef?: RefObject<HTMLUListElement | undefined>
  currentSort?: SortOrderType
  onSortChange?: (sort: SortOrderType) => void
}

export function ArtifactsList(props: ArtifactsListProps) {
  const {org, artifacts, totalCount, listRef, currentSort, onSortChange} = props

  const listTitle = `${formatNumber(totalCount)} ${totalCount === 1 ? 'artifact' : 'artifacts'}`

  const {field: currentField, direction: currentDirection} = parseSortOrder(currentSort ?? DEFAULT_SORT)

  const isDescending = currentDirection === 'desc'

  const buttonLabel = (() => {
    const label = SORT_BUTTON_LABELS[currentField]
    return typeof label === 'string' ? label : label[currentDirection]
  })()

  const sortDropdown = artifacts.length > 0 && onSortChange && (
    <ActionMenu>
      <ActionMenu.Button
        leadingVisual={isDescending ? SortDescIcon : SortAscIcon}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className="fg-muted"
        variant="invisible"
        aria-label={`Sort by ${SORT_FIELD_LABELS[currentField]}, ${SORT_DIRECTION_LABELS[currentDirection]} first`}
      >
        {buttonLabel}
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Sort by</ActionList.GroupHeading>
            {(Object.entries(SORT_FIELD_LABELS) as Array<[SortField, string]>).map(([field, label]) => (
              <ActionList.Item
                key={field}
                onSelect={() => onSortChange(buildSortOrder(field, currentDirection))}
                selected={currentField === field}
              >
                {label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
          <ActionList.Divider />
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Order</ActionList.GroupHeading>
            {(Object.entries(SORT_DIRECTION_LABELS) as Array<[SortDirection, string]>).map(([direction, label]) => (
              <ActionList.Item
                key={direction}
                onSelect={() => onSortChange(buildSortOrder(currentField, direction))}
                selected={currentDirection === direction}
              >
                <ActionList.LeadingVisual>
                  {direction === 'desc' ? <SortDescIcon /> : <SortAscIcon />}
                </ActionList.LeadingVisual>
                {label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  return (
    <ListView
      variant="default"
      title="Artifacts list"
      titleHeaderTag="h3"
      className="border rounded"
      metadata={<ListViewMetadata title={listTitle}>{sortDropdown}</ListViewMetadata>}
      listRef={listRef}
      emptyState={
        <div className="blankslate" role="region" aria-label="No results" aria-live="polite" aria-atomic="true">
          <h3 className="blankslate-heading">No results</h3>
          <p>Try adjusting your search filters.</p>
        </div>
      }
    >
      {artifacts.map(artifact => (
        <ArtifactListItem
          key={`artifact-${artifact.artifact_metadata_id}`}
          artifact={artifact}
          org={org}
          sortField={currentField}
        />
      ))}
    </ListView>
  )
}
